import { create } from 'zustand'
import {
  beginLogin,
  exchangeCodeForTokens,
  refreshTokens,
  getStoredTokens,
  isExpired,
  clearTokens,
  signOut,
  decodeIdToken,
  persistSessionTokens,
} from '@/lib/oauth'
import { authenticateUser, type AuthResult } from '@/lib/cognito'
import { markPasswordSignIn, signInWithPasskey } from '@/lib/passkey'
import type { CognitoUserSession } from 'amazon-cognito-identity-js'
import { broadcastAuthReset } from '@/pwa/tab-sync'
import { resetChatStorage } from '@/features/chats/services/matrix-chat'
import { recordLogin } from '@/lib/lastLogin'

/**
 * Cognito group that gates access to N.E.S.T. A valid Cognito sign-in is
 * necessary but not sufficient: the ID token's `cognito:groups` claim must
 * contain this group. Source of truth is the Cognito user pool.
 */
export const NEST_ACCESS_GROUP = 'nest-access'

/**
 * Thrown when a user authenticates against Cognito but is not in the
 * `nest-access` group. The flow clears the local tokens first so a
 * half-authenticated state can't linger.
 */
export class NoNestAccessError extends Error {
  constructor(public readonly email: string) {
    super(`Account ${email} is not authorized to access N.E.S.T.`)
    this.name = 'NoNestAccessError'
    Object.setPrototypeOf(this, NoNestAccessError.prototype)
  }
}

/**
 * Thrown when the ID token comes from a federated IdP (Apple / Google). N.E.S.T.
 * is email + password only until the monetization work ships (#701); the app
 * clients are shared with mobile, so this cannot be switched off in Cognito.
 */
export class FederatedSignInDisabledError extends Error {
  constructor() {
    super('Apple and Google sign-in are not available for N.E.S.T. Use email and password.')
    this.name = 'FederatedSignInDisabledError'
    Object.setPrototypeOf(this, FederatedSignInDisabledError.prototype)
  }
}

/** Cognito puts an `identities` claim on every federated user's ID token. */
function isFederated(payload: Record<string, unknown>): boolean {
  const ids = payload['identities']
  return Array.isArray(ids) ? ids.length > 0 : ids != null
}

function hasNestAccess(payload: Record<string, unknown>): boolean {
  if (isFederated(payload)) return false
  const groups = (payload['cognito:groups'] as string[] | undefined) ?? []
  return groups.includes(NEST_ACCESS_GROUP)
}

/** The error an interactive sign-in raises when hasNestAccess refuses it. */
function accessError(payload: Record<string, unknown>): Error {
  return isFederated(payload)
    ? new FederatedSignInDisabledError()
    : new NoNestAccessError((payload['email'] as string) ?? '')
}

/** Minimal setter surface so the shared finisher can update store state. */
type AuthSet = (partial: Partial<AuthState>) => void

/**
 * Shared completion for any non-hosted-UI sign-in (in-app password, passkey):
 * persist tokens in the oauth shape, enforce nest-access, stamp recordLogin, and
 * flip to authenticated. Mirrors completeAuth so all paths converge identically.
 */
async function finishWithTokens(
  set: AuthSet,
  tokens: {
    idToken: string
    accessToken: string
    refreshToken?: string
    expiresAtSeconds?: number
  },
): Promise<void> {
  set({ isLoading: true })
  try {
    const payload = decodeIdToken(tokens.idToken)
    if (!hasNestAccess(payload)) {
      clearTokens()
      set({ user: null, idToken: null, isAuthenticated: false, isLoading: false })
      throw accessError(payload)
    }
    persistSessionTokens({
      idToken: tokens.idToken,
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      expiresAtSeconds: tokens.expiresAtSeconds,
    })
    recordLogin(payload['sub'] as string | undefined)
    set({
      user: { email: (payload['email'] as string) ?? '' },
      idToken: tokens.idToken,
      isAuthenticated: true,
      isLoading: false,
      passwordChallenge: null,
    })
  } catch (err) {
    set({ isLoading: false })
    throw err
  }
}

// Carries the state needed to drive a Cognito NEW_PASSWORD_REQUIRED challenge
// from the set-new-password screen. The terminal login's in-app email/password
// path stamps this when Cognito challenges a FORCE_CHANGE_PASSWORD account; the
// hosted-UI OAuth path never sets it (Cognito's UI owns first-time passwords).
interface PasswordChallenge {
  email: string
}

interface AuthState {
  user: { email: string } | null
  idToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  /** Always null under hosted-UI OAuth (new-password is handled by Cognito). */
  passwordChallenge: PasswordChallenge | null
  /**
   * Redirect to the Cognito hosted UI. Single login; SSOs with /ops + /analytics.
   * `identityProvider` pins a federated IdP (e.g. 'Google', 'SignInWithApple')
   * so Cognito's selection UI is skipped and the user goes straight to the IdP.
   */
  loginRedirect: (returnTo?: string, identityProvider?: string) => Promise<void>
  /** Called by /auth/callback with the authorization code. */
  completeAuth: (code: string) => Promise<void>
  /**
   * In-app email/password sign-in (no hosted-UI redirect). Drives the SDK's
   * authenticateUser; on a clean session it finishes auth the SAME way
   * completeAuth does (tokens persisted in the oauth shape, nest-access enforced,
   * recordLogin stamped). A FORCE_CHANGE_PASSWORD account returns
   * 'newPasswordRequired' and stamps `passwordChallenge` for the set-new-password
   * screen instead of authenticating.
   */
  signInWithPassword: (email: string, password: string) => Promise<AuthResult>
  /**
   * Finish authentication from a CognitoUserSession obtained outside the OAuth
   * code exchange (in-app password sign-in, or a direct USER_AUTH / WebAuthn
   * challenge response). Persists tokens, enforces nest-access, records login.
   */
  completeWithSession: (session: CognitoUserSession) => Promise<void>
  /**
   * Passkey / Face ID (WebAuthn) sign-in via Cognito USER_AUTH. On success the
   * tokens are finished the same way as every other path. Re-throws
   * PasskeyNotEnrolledError so the UI can show a non-silent fallback prompt.
   */
  completeWithPasskey: (email: string) => Promise<void>
  /** Deprecated (hosted UI handles it) — kept as a compile-safe stub. */
  completeNewPassword: (newPassword: string, attrs?: Record<string, string>) => Promise<void>
  clearPasswordChallenge: () => void
  logout: () => void
  logoutAndReset: () => Promise<void>
  restoreSession: () => Promise<void>
  refreshToken: () => Promise<void>
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  idToken: null,
  isAuthenticated: false,
  isLoading: true,
  passwordChallenge: null,

  loginRedirect: async (returnTo = '/', identityProvider?: string) => {
    await beginLogin(returnTo, identityProvider)
  },

  completeAuth: async (code: string) => {
    set({ isLoading: true })
    try {
      const tokens = await exchangeCodeForTokens(code)
      const payload = decodeIdToken(tokens.idToken)
      if (!hasNestAccess(payload)) {
        // Authenticated to Cognito but not in nest-access — clear and surface.
        clearTokens()
        set({ user: null, idToken: null, isAuthenticated: false, isLoading: false })
        throw accessError(payload)
      }
      // Stamp the last successful login to THIS origin (per user) — this is a
      // real interactive sign-in, not a silent restore. The splash reads it so
      // "LAST LOGIN" reflects this URL, not the pool-wide auth_time (which SSO
      // shares across apps).
      recordLogin(payload['sub'] as string | undefined)
      set({
        user: { email: (payload['email'] as string) ?? '' },
        idToken: tokens.idToken,
        isAuthenticated: true,
        isLoading: false,
      })
    } catch (err) {
      set({ isLoading: false })
      throw err
    }
  },

  signInWithPassword: async (email: string, password: string) => {
    set({ isLoading: true })
    try {
      const result = await authenticateUser(email, password)
      if (result.kind === 'session') {
        await get().completeWithSession(result.session)
        markPasswordSignIn()
        return result
      }
      // FORCE_CHANGE_PASSWORD: hand off to the set-new-password screen. We don't
      // hold the CognitoUser instance in the store (it isn't serialisable and the
      // challenge is completed on a dedicated screen via the SDK), so we only
      // stamp the email; the screen re-initiates if it needs the live instance.
      set({ passwordChallenge: { email }, isLoading: false })
      return result
    } catch (err) {
      set({ isLoading: false })
      throw err
    }
  },

  completeWithSession: async (session: CognitoUserSession) => {
    await finishWithTokens(set, {
      idToken: session.getIdToken().getJwtToken(),
      accessToken: session.getAccessToken().getJwtToken(),
      refreshToken: session.getRefreshToken().getToken(),
      // getExpiration() is absolute epoch SECONDS.
      expiresAtSeconds: session.getAccessToken().getExpiration(),
    })
  },

  completeWithPasskey: async (email: string) => {
    set({ isLoading: true })
    let tokens
    try {
      tokens = await signInWithPasskey(email)
    } catch (err) {
      set({ isLoading: false })
      throw err // PasskeyNotEnrolledError (handled by UI) or a genuine failure
    }
    // The USER_AUTH response carries a RELATIVE ExpiresIn; convert to the
    // absolute epoch-seconds finishWithTokens expects.
    const expiresAtSeconds = tokens.expiresInSeconds
      ? Math.floor(Date.now() / 1000) + tokens.expiresInSeconds
      : undefined
    await finishWithTokens(set, {
      idToken: tokens.idToken,
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      expiresAtSeconds,
    })
  },

  completeNewPassword: async () => {
    throw new Error('Password changes are handled by the hosted sign-in.')
  },
  clearPasswordChallenge: () => set({ passwordChallenge: null }),

  logout: () => {
    broadcastAuthReset()
    set({ user: null, idToken: null, isAuthenticated: false })
    signOut() // clears tokens, revokes the refresh token, goes to /sign-in
  },

  /**
   * Full sign-out + chat-storage teardown. Use when persisted Matrix client
   * state was minted under the wrong pool/issuer and a plain logout() would
   * leave the next sign-in stuck on "Chat authentication failed".
   */
  logoutAndReset: async () => {
    await resetChatStorage()
    broadcastAuthReset()
    set({ user: null, idToken: null, isAuthenticated: false })
    signOut()
  },

  /**
   * Keep the ID token fresh from the refresh token. The ID token expires
   * ~hourly; without this every authenticated nest-api call would start 401ing
   * and empty the app. Defensive: a transient failure leaves state untouched.
   */
  refreshToken: async () => {
    if (!get().isAuthenticated) return
    try {
      const tokens = await refreshTokens()
      if (!tokens) return
      const payload = decodeIdToken(tokens.idToken)
      if (!hasNestAccess(payload)) return
      if (tokens.idToken !== get().idToken) {
        set({
          idToken: tokens.idToken,
          isAuthenticated: true,
          user: { email: (payload['email'] as string) ?? get().user?.email ?? '' },
        })
      }
    } catch {
      // Transient — keep the existing token; the interval retries shortly.
    }
  },

  restoreSession: async () => {
    if (get().isAuthenticated) {
      set({ isLoading: false })
      return
    }
    set({ isLoading: true })
    let tokens = getStoredTokens()
    if (tokens && isExpired(tokens)) {
      tokens = await refreshTokens()
    }
    if (tokens && !isExpired(tokens)) {
      const payload = decodeIdToken(tokens.idToken)
      if (hasNestAccess(payload)) {
        // Authenticated load (restored session) — track the dev-nest session so
        // the splash's "last login" reflects actual visits, not the frozen
        // auth_time claim.
        recordLogin(payload['sub'] as string | undefined)
        set({
          user: { email: (payload['email'] as string) ?? '' },
          idToken: tokens.idToken,
          isAuthenticated: true,
          isLoading: false,
        })
        return
      }
      // Valid session but no longer in nest-access (revoked between sessions).
      clearTokens()
    }
    set({ user: null, idToken: null, isAuthenticated: false, isLoading: false })
  },
}))
